import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Send, Loader2, X, Minimize2, Maximize2, MessageSquare } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocation } from "react-router-dom";
import { getBaseUrl } from "@/lib/config";

interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
}

const MODULE_MAP: Record<string, string> = {
  "/": "dashboard",
  "/dashboard": "dashboard",
  "/work-orders": "work_orders",
  "/inventory": "inventory",
  "/materials": "inventory",
  "/plybooks": "plybooks",
  "/quality": "quality",
  "/products": "products",
  "/processes": "processes",
  "/customers": "customers",
  "/projects": "projects",
  "/budgets": "budgets",
  "/reports": "reports",
};

export function AIFloatingChat() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const moduleId = MODULE_MAP[location.pathname] || "dashboard";
  const baseUrl = getBaseUrl();

  const { data: healthData } = useQuery({
    queryKey: ["ai-health"],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}/api/ai/health`);
      if (!res.ok) return { ready: false };
      return res.json();
    },
    staleTime: 60 * 1000,
    retry: false,
  });

  const { data: suggestionsData } = useQuery({
    queryKey: ["ai-suggestions", moduleId],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}/api/ai/suggestions/${moduleId}`);
      if (!res.ok) return { suggestions: [] };
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    enabled: isOpen && healthData?.ready,
  });

  const chatMutation = useMutation({
    mutationFn: async (message: string) => {
      const res = await fetch(`${baseUrl}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message,
          moduleId,
          conversationHistory: messages.slice(-10).map(m => ({ role: m.role, content: m.content })),
          userId: user?.id,
        }),
      });
      if (!res.ok) throw new Error("Chat failed");
      return res.json();
    },
    onSuccess: (data) => {
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: data.response, timestamp: new Date().toISOString() },
      ]);
    },
    onError: () => {
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: t.ai.error, timestamp: new Date().toISOString() },
      ]);
    },
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  const handleSend = () => {
    if (!input.trim() || chatMutation.isPending) return;
    const userMessage: Message = { role: "user", content: input, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    chatMutation.mutate(input);
    setInput("");
  };

  const handleSuggestion = (suggestion: string) => {
    setInput(suggestion);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  if (!healthData?.ready) return null;

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 z-50"
        size="icon"
      >
        <Sparkles className="h-6 w-6 text-white" />
      </Button>
    );
  }

  if (isMinimized) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <Card className="w-72 shadow-xl border-purple-200 dark:border-purple-800">
          <CardHeader className="p-3 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <Sparkles className="h-4 w-4" />
                <span className="font-medium text-sm">{t.ai.assistant}</span>
                {messages.length > 0 && (
                  <Badge variant="secondary" className="text-xs h-5">{messages.length}</Badge>
                )}
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-6 w-6 text-white hover:bg-white/20" onClick={() => setIsMinimized(false)}>
                  <Maximize2 className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-6 w-6 text-white hover:bg-white/20" onClick={() => setIsOpen(false)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <Card className="w-96 shadow-2xl border-purple-200 dark:border-purple-800 max-h-[600px] flex flex-col">
        <CardHeader className="p-3 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-t-lg flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <Sparkles className="h-5 w-5" />
              <span className="font-semibold">{t.ai.assistant}</span>
              <Badge variant="secondary" className="text-xs capitalize">{moduleId.replace("_", " ")}</Badge>
            </div>
            <div className="flex gap-1">
              {messages.length > 0 && (
                <Button variant="ghost" size="icon" className="h-7 w-7 text-white hover:bg-white/20" onClick={clearChat} title={t.ai.clearChat}>
                  <MessageSquare className="h-4 w-4" />
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-7 w-7 text-white hover:bg-white/20" onClick={() => setIsMinimized(true)}>
                <Minimize2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-white hover:bg-white/20" onClick={() => setIsOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-3 flex-1 overflow-hidden flex flex-col">
          {messages.length === 0 ? (
            <div className="space-y-3 flex-1">
              <p className="text-sm text-muted-foreground">
                {t.ai.greeting}
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>- {t.ai.capabilities.analyzeProduction}</li>
                <li>- {t.ai.capabilities.answerQuestions}</li>
                <li>- {t.ai.capabilities.suggestOptimizations}</li>
              </ul>
              {suggestionsData?.suggestions && suggestionsData.suggestions.length > 0 && (
                <div className="pt-2">
                  <p className="text-xs text-muted-foreground mb-2">{t.ai.suggestions}:</p>
                  <div className="flex flex-wrap gap-1">
                    {suggestionsData.suggestions.map((s: string, i: number) => (
                      <Button
                        key={i}
                        variant="outline"
                        size="sm"
                        onClick={() => handleSuggestion(s)}
                        className="text-xs h-7 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                      >
                        {s}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <ScrollArea className="flex-1 pr-2 -mr-2" ref={scrollRef}>
              <div className="space-y-3">
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`p-3 rounded-lg text-sm ${
                      m.role === "user"
                        ? "bg-purple-100 dark:bg-purple-900/40 text-purple-900 dark:text-purple-100 ml-8"
                        : "bg-gray-100 dark:bg-gray-800 mr-8"
                    }`}
                  >
                    <div className="whitespace-pre-wrap">{m.content}</div>
                  </div>
                ))}
                {chatMutation.isPending && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-gray-100 dark:bg-gray-800 rounded-lg mr-8">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t.ai.thinking}
                  </div>
                )}
              </div>
            </ScrollArea>
          )}

          <div className="flex gap-2 pt-3 mt-auto flex-shrink-0">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t.ai.placeholder}
              disabled={chatMutation.isPending}
              className="text-sm"
            />
            <Button
              size="icon"
              onClick={handleSend}
              disabled={!input.trim() || chatMutation.isPending}
              className="bg-purple-600 hover:bg-purple-700 flex-shrink-0"
            >
              {chatMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}